/**
 * Framework-agnostic Polotno design comparison.
 *
 * Used server-side to decide whether a saved label is byte-for-intent identical
 * to the migration generator's output (content gate). Mirrors Polotno's own
 * change-detection: `polotno/model/history.js`'s UndoManager diffs the snapshot
 * subset `{ pages, width, height, custom, audios }` with a key-order-insensitive
 * `deepEqual`, deliberately ignoring `fonts`/`unit`/`dpi`/`bleed`/`schemaVersion`
 * (those drift on `loadJSON`→`toJSON` and are not user-editable design content).
 * We re-implement that primitive here so a server package needn't depend on the
 * browser-only Polotno SDK.
 *
 * Guide stripping and background-alpha normalization are Supliful concerns (editor
 * decorations / print constraints), so they live here rather than in Polotno.
 */

/** Single source of truth for all guide element IDs. */
export const GUIDE_IDS = [
  "safe-area",
  "safe-area-alt",
  "trim-line",
  "trim-mask",
  "matrix-zone",
  // The data-matrix placeholder rendered under the matrix-zone overlay. A full
  // guide element (locked, raised, stripped from saves) but deliberately NOT
  // in MATRIX_GUIDE_IDS: the legend's matrix toggle hides only the colored
  // overlay, while the placeholder stays visible.
  "matrix-reserved",
] as const

const GUIDE_ID_SET = new Set<string>(GUIDE_IDS)

/** Safe-area guide IDs (both the main frame and its alt variant). */
export const SAFE_AREA_GUIDE_IDS = ["safe-area", "safe-area-alt"] as const

/** Trim-line guide IDs (die-cut boundary line for rounded-corner labels). */
export const TRIM_LINE_GUIDE_IDS = ["trim-line"] as const

/**
 * Trim-mask guide IDs. The mask fills the corner slivers outside a rounded die
 * cut so the non-printable corners read as bleed rather than printable white.
 */
export const TRIM_MASK_GUIDE_IDS = ["trim-mask"] as const

/** Matrix-code zone guide IDs. */
export const MATRIX_GUIDE_IDS = ["matrix-zone"] as const

const SAFE_AREA_GUIDE_ID_SET = new Set<string>(SAFE_AREA_GUIDE_IDS)
const TRIM_LINE_GUIDE_ID_SET = new Set<string>(TRIM_LINE_GUIDE_IDS)
const TRIM_MASK_GUIDE_ID_SET = new Set<string>(TRIM_MASK_GUIDE_IDS)
const MATRIX_GUIDE_ID_SET = new Set<string>(MATRIX_GUIDE_IDS)

/** Check if an element is a guide element. */
export function isGuideElement(id: string): boolean {
  return GUIDE_ID_SET.has(id)
}

/** Check if an element is a safe-area guide. */
export function isSafeAreaGuideElement(id: string): boolean {
  return SAFE_AREA_GUIDE_ID_SET.has(id)
}

/** Check if an element is a trim-line guide. */
export function isTrimLineGuideElement(id: string): boolean {
  return TRIM_LINE_GUIDE_ID_SET.has(id)
}

/** Check if an element is a trim-mask guide (rounded-corner bleed mask). */
export function isTrimMaskGuideElement(id: string): boolean {
  return TRIM_MASK_GUIDE_ID_SET.has(id)
}

/** Check if an element is a matrix-code zone guide. */
export function isMatrixGuideElement(id: string): boolean {
  return MATRIX_GUIDE_ID_SET.has(id)
}

/**
 * True for template-embedded guide elements (marked with `custom.guide === true`).
 * Distinct from {@link isGuideElement}, which keys off the legacy hardcoded IDs.
 * Both kinds are stripped before persistence and re-injected from the template on load.
 */
export function isTemplateGuideElement(child: unknown): boolean {
  if (typeof child !== "object" || child === null) return false
  const custom = (child as { custom?: unknown }).custom
  return (
    typeof custom === "object" &&
    custom !== null &&
    (custom as { guide?: unknown }).guide === true
  )
}

const rgbChannel = "(?:\\d+(?:\\.\\d+)?|\\.\\d+)%?"
const alphaChannel = "(?:0(?:\\.\\d+)?|1(?:\\.0+)?|\\.\\d+)"
const rgbaPattern = new RegExp(
  `rgba\\(\\s*(${rgbChannel})\\s*,\\s*(${rgbChannel})\\s*,\\s*(${rgbChannel})\\s*,\\s*${alphaChannel}\\s*\\)`,
  "gi",
)
const hslaPattern =
  /hsla\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*([^,]+)\s*,\s*(?:0(?:\.\d+)?|1(?:\.0+)?|\.\d+)\s*\)/gi
const hexWithAlphaPattern = /#([\da-f]{6})[\da-f]{2}\b/gi

/** Convert color values that include alpha channels into opaque equivalents. */
export function removeColorAlpha(value: string): string {
  if (/^(?:https?|data|blob):/i.test(value)) return value
  return value
    .replace(/\btransparent\b/gi, "white")
    .replace(rgbaPattern, "rgb($1, $2, $3)")
    .replace(hslaPattern, "hsl($1, $2, $3)")
    .replace(hexWithAlphaPattern, "#$1")
}

// Polotno's UndoManager change-detection subset (model/history.js). Anything
// outside this set is treated as non-content and excluded from the comparison.
const CONTENT_KEYS = ["pages", "width", "height", "custom", "audios"] as const

/**
 * Normalize a Polotno store JSON down to its comparable design content, or
 * `null` when the input is structurally invalid (non-object / missing `pages`).
 * A `null` result means "never equal" so a malformed payload fails safe to
 * manual verification.
 */
export function canonicalizePolotnoContent(
  json: unknown,
): Record<string, unknown> | null {
  if (!json || typeof json !== "object") return null
  const root = json as Record<string, unknown>
  if (!Array.isArray(root.pages)) return null

  const pages = root.pages.map((page) => {
    const p = { ...((page ?? {}) as Record<string, unknown>) }
    // Platform print spec, not design content: the editor always injects it,
    // generators omit it, schema normalization zero-fills it — same reasoning
    // as the root-level `bleed` above.
    delete p.bleed
    if (Array.isArray(p.children)) {
      p.children = p.children.filter((child) => {
        const id = (child as { id?: string } | null)?.id ?? ""
        return !isGuideElement(id) && !isTemplateGuideElement(child)
      })
    }
    if (typeof p.background === "string") {
      p.background = removeColorAlpha(p.background)
    }
    return p
  })

  const out: Record<string, unknown> = { pages }
  for (const key of CONTENT_KEYS) {
    if (key !== "pages" && key in root) out[key] = root[key]
  }
  return out
}

/** Own keys whose value is not `undefined` — i.e. the keys JSON would emit. */
const jsonKeys = (o: Record<string, unknown>): string[] =>
  Object.keys(o).filter((k) => o[k] !== undefined)

// Polotno recomputes group-child coordinates through the group's transform
// matrix on every loadJSON→toJSON cycle; the same design can drift by small
// sub-pixel amounts (e.g. 917.9599999999999 vs 918). Compare numbers within a
// combined absolute + relative epsilon so this noise reads as equal while larger
// real moves are still caught. Non-finite values (NaN/Infinity) require an exact
// match.
const NUMERIC_ABS_EPSILON = 0.05
const NUMERIC_REL_EPSILON = 1e-9
const numbersClose = (a: number, b: number): boolean => {
  if (a === b) return true
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false
  return (
    Math.abs(a - b) <=
    NUMERIC_ABS_EPSILON +
      NUMERIC_REL_EPSILON * Math.max(Math.abs(a), Math.abs(b))
  )
}

// Mirrors polotno/utils/deep-equal: key-order insensitive, array-order sensitive.
// A key set to `undefined` is treated as absent (JSON semantics — `JSON.stringify`
// drops it). This matters because the editor's `store.toJSON()` emits `undefined`
// props that survive to the server via superjson, whereas Postgres JSONB and the
// generator output omit them; without this, an unedited label reads as changed.
function deepEqual(x: unknown, y: unknown): boolean {
  if (x === y) return true
  if (typeof x === "number" && typeof y === "number") return numbersClose(x, y)
  if (
    typeof x !== "object" ||
    x === null ||
    typeof y !== "object" ||
    y === null
  ) {
    return false
  }
  const xr = x as Record<string, unknown>
  const yr = y as Record<string, unknown>
  const xKeys = jsonKeys(xr)
  if (xKeys.length !== jsonKeys(yr).length) return false
  for (const key of xKeys) {
    if (!deepEqual(xr[key], yr[key])) return false
  }
  return true
}

/**
 * True only when both inputs are structurally valid Polotno store JSON and
 * their normalized design content is deep-equal.
 */
export function polotnoDesignEqual(a: unknown, b: unknown): boolean {
  const ca = canonicalizePolotnoContent(a)
  const cb = canonicalizePolotnoContent(b)
  return ca !== null && cb !== null && deepEqual(ca, cb)
}
